import { FormWidgetTextTheme } from "@xapp/form-widget";

import { sameColor } from "./color";

/**
 * Which colours the stand-alone page's business header is drawn in.
 *
 * The header belongs to the business. A partner style preset dresses the FORM
 * -- that is the whole of its remit on this page -- so when both have something
 * to say about the header, the business wins.
 *
 * This matters because a partner preset merges *over* the customer's theme
 * (`mergeFormWidgetTheme`), which is right for the form and wrong here: it
 * painted a dealer's masthead in their partner's colours, and the business
 * disappeared from its own booking page.
 *
 * The relationship gets its own band under the header instead, which is where
 * it belongs. That band is `FormWidgetEnv.subHeader`, and it lands in a sibling
 * PR (XappMedia/chat-widget#1697) rather than this one — so **that must merge
 * first**, or a partnered page loses the partner from its header with nothing
 * yet drawing it below.
 *
 * Background and text are taken from the SAME source, never mixed. Taking the
 * business's background and the preset's text colour is how you get a page that
 * passes both halves of a review and is unreadable in production.
 *
 * A business that sets no header colour keeps whatever it had, preset included,
 * so no page changes appearance until someone gives it a colour of its own.
 */
export function resolveStandAloneHeader(
    /** The business's own widget theme, before any preset merged over it. */
    businessHeader: FormWidgetTextTheme | undefined,
    /** The header as the preset left it. */
    resolvedHeader: FormWidgetTextTheme | undefined,
    /** The page background, used when neither names a colour. */
    fallbackBackgroundColor: string,
    /**
     * Contrast fallback, applied when the chosen source has no readable text
     * colour. May answer nothing -- `invertColor` returns undefined for a colour
     * it cannot read -- in which case the header inherits rather than being
     * given a colour computed from a value nobody could parse.
     */
    invert: (color: string) => string | undefined
): { backgroundColor: string; color: string | undefined } {
    const source = businessHeader?.backgroundColor ? businessHeader : resolvedHeader;

    const backgroundColor = source?.backgroundColor || fallbackBackgroundColor || "#ffffff";

    // Same rule as before: no colour, or one indistinguishable from what it sits
    // on, is not a colour -- it is an invisible business name.
    // sameColor, not !==: "#b4337a" and "#B4337A" are one colour written two
    // ways, as are "#fff" and "#ffffff". Answering "different" there skips the
    // inversion and renders the business name invisible on its own header --
    // the exact bug this guard exists to catch, and the one the header's
    // previous inline version was fixed for before this resolver took it over.
    const color =
        source?.color && !sameColor(source.color, backgroundColor)
            ? source.color
            : invert(backgroundColor);

    return { backgroundColor, color };
}
